/**
 * Nold Go — Covoiturage : landing marketing publique + CGV + Chat modal.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Euro, ShieldCheck, Star, Users, ArrowRight, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEOHead from "../components/SEOHead";

// ================================================= LANDING
export function CarpoolLanding() {
  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-landing">
      <SEOHead
        title="Covoiturage longue distance en Europe — Nold Go"
        description="Rejoignez la nouvelle plateforme belge de covoiturage entre grandes villes européennes. Bruxelles, Paris, Amsterdam, Berlin, Londres, Madrid. Prix au coût réel, conducteurs vérifiés, 10 % commission seulement."
        canonical="/covoiturage/decouvrir"
      />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-24">
        <p className="text-xs uppercase tracking-widest text-[#F26419] font-bold mb-3">Nouveau · Nold Go Covoiturage</p>
        <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black text-[#1A1A1A] leading-tight">
          Partagez la route.<br />
          <span className="text-[#059669]">Divisez les frais.</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#3B423E] max-w-2xl">
          La nouvelle plateforme belge de covoiturage entre grandes villes européennes.
          Prix au coût réel (aucun bénéfice conducteur), conducteurs vérifiés, paiement sécurisé Stripe.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/covoiturage" data-testid="landing-cta-search"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm">
            <Car className="w-4 h-4" /> Rechercher un trajet <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/covoiturage/publier" data-testid="landing-cta-publish"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-[#F26419] text-[#F26419] font-bold text-sm hover:bg-[#F26419]/5">
            <Euro className="w-4 h-4" /> Proposer un trajet
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          { icon: Euro, title: "Prix au coût réel", text: "Plafonné à 0,12 €/km divisé par le nombre de places — aucun bénéfice conducteur. Vraiment économique." },
          { icon: ShieldCheck, title: "Conducteurs vérifiés", text: "Permis + carte grise + pièce d'identité validés par notre équipe. Badge « Vérifié » visible avant de réserver." },
          { icon: Star, title: "Avis authentiques", text: "Notation bidirectionnelle après chaque trajet, moyenne affichée sur le profil du conducteur." },
        ].map(({ icon: I, title, text }) => (
          <div key={title} className="bg-white rounded-2xl p-6 border border-[#E2DFD8]">
            <div className="w-12 h-12 rounded-2xl bg-[#F26419]/10 flex items-center justify-center mb-3">
              <I className="w-6 h-6 text-[#F26419]" />
            </div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A] mb-1">{title}</h3>
            <p className="text-sm text-[#3B423E]">{text}</p>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="font-head text-3xl font-black text-[#1A1A1A] mb-8">Comment ça marche ?</h2>
        <ol className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            "Créez votre profil (prénom + téléphone vérifié SMS)",
            "Recherchez ou publiez un trajet entre grandes villes",
            "Réservez par carte via Stripe (10 % commission)",
            "Voyagez, discutez, notez — reconstruisez la confiance",
          ].map((step, i) => (
            <li key={i} className="bg-white rounded-2xl p-5 border border-[#E2DFD8]">
              <div className="text-3xl font-black text-[#059669] mb-2">{i + 1}</div>
              <p className="text-sm text-[#3B423E]">{step}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="font-head text-3xl font-black text-[#1A1A1A] mb-4">Nos destinations</h2>
        <p className="text-sm text-[#3B423E] mb-6">Bruxelles est notre point de départ favori. Rejoignez Paris, Amsterdam, Luxembourg, Cologne, Londres, Berlin, Madrid, Rome.</p>
        <div className="flex flex-wrap gap-3 mb-5">
          {[["bruxelles-paris", "Bruxelles → Paris"], ["paris-bruxelles", "Paris → Bruxelles"]].map(([s, l]) => (
            <Link key={s} to={`/covoiturage/${s}`} data-testid={`landing-route-${s}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#059669] hover:bg-[#047857] text-white text-sm font-bold transition-colors">
              Covoiturage {l} <ArrowRight className="w-4 h-4" />
            </Link>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {["Bruxelles", "Paris", "Amsterdam", "Luxembourg", "Cologne", "Londres", "Berlin", "Madrid", "Rome"].map((c) => (
            <span key={c} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E2DFD8] text-xs font-bold text-[#3B423E]">
              <Users className="w-3 h-3" /> {c}
            </span>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-12">
        <h2 className="font-head text-3xl font-black text-[#1A1A1A] mb-6">Questions fréquentes</h2>
        <div className="space-y-4">
          {[
            ["Comment est fixé le prix ?", "Le prix maximum par place = (distance × 0,12 €/km) ÷ nombre de places. C'est le coût réel moyen d'un trajet longue distance. Aucun conducteur ne peut gagner d'argent — c'est du partage de frais pur."],
            ["Puis-je annuler ?", "Oui, jusqu'à 24 h avant le départ pour un remboursement complet. Après, les frais sont acquis au conducteur (BlaBlaCar-style). Si c'est le conducteur qui annule, vous êtes remboursé intégralement."],
            ["Comment fonctionnent les paiements ?", "Stripe encaisse le paiement. 10 % restent chez Nold Go, 90 % sont versés au conducteur (Stripe Connect Express) une fois qu'il a onboardé son compte bancaire."],
            ["Est-ce sécurisé ?", "Chaque conducteur upload son permis, sa carte grise et une pièce d'identité, validés manuellement par notre équipe. Vous voyez la note moyenne et les avis avant de réserver."],
          ].map(([q, a]) => (
            <details key={q} className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
              <summary className="font-bold cursor-pointer text-[#1A1A1A]">{q}</summary>
              <p className="mt-2 text-sm text-[#3B423E]">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16 text-center">
        <h2 className="font-head text-4xl font-black text-[#1A1A1A] mb-4">Prêt à partir ?</h2>
        <p className="text-[#3B423E] mb-6">Rejoignez la communauté Nold Go Covoiturage aujourd'hui.</p>
        <Link to="/covoiturage" data-testid="landing-cta-bottom"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#F26419] hover:bg-[#D85312] text-white font-bold">
          Trouver mon prochain trajet <ArrowRight className="w-5 h-5" />
        </Link>
        <p className="mt-8 text-xs text-[#75807A]">
          <Link to="/covoiturage/cgv" className="underline">Conditions générales spécifiques au covoiturage</Link>
        </p>
      </section>
    </div>
  );
}

// ================================================= CGV
export function CarpoolTerms() {
  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-cgv">
      <SEOHead title="Conditions générales du service de covoiturage — Nold Go" description="Conditions générales spécifiques au service de covoiturage longue distance Nold Go." canonical="/covoiturage/cgv" />
      <div className="max-w-3xl mx-auto px-4 py-12 text-sm text-[#3B423E] leading-relaxed">
        <h1 className="font-head text-3xl font-black text-[#1A1A1A] mb-2">Conditions générales — Nold Go Covoiturage</h1>
        <p className="text-xs text-[#75807A] mb-8">Version applicable · dernière mise à jour : 22 septembre 2026</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">1. Nature du service</h2>
        <p>Nold Go Covoiturage est une plateforme de mise en relation entre conducteurs particuliers et passagers pour un partage de frais sur des trajets longue distance en Europe. <b>Le service n'est pas une activité de transport commercial</b>. Aucun conducteur ne peut réaliser de bénéfice : le prix par place est plafonné automatiquement à (distance × 0,12 €/km) ÷ nombre de places.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">2. Éligibilité conducteur</h2>
        <p>Le conducteur déclare : (a) détenir un permis de conduire européen valide, (b) être propriétaire ou utilisateur légitime du véhicule qu'il propose, (c) disposer d'une assurance auto en cours de validité couvrant le transport de passagers à titre non lucratif. Nold Go vérifie manuellement le permis, la carte grise et une pièce d'identité mais n'agit pas comme assureur.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">3. Paiement, commission, reversement</h2>
        <p>Le paiement est perçu par Nold Go via Stripe. Nold Go conserve une commission de <b>10 %</b> du montant TTC. Les 90 % restants sont automatiquement reversés au conducteur via Stripe Connect Express (compte bancaire européen SEPA requis).</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">4. Annulation</h2>
        <p><b>Passager :</b> annulation possible jusqu'à 24 h avant le départ → remboursement intégral. En deçà de 24 h : aucun remboursement (les frais du conducteur sont acquis).<br />
        <b>Conducteur :</b> annulation possible à tout moment mais entraîne le remboursement intégral de tous les passagers payés. Les annulations répétées peuvent entraîner la suspension du compte.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">5. Comportement, non-discrimination</h2>
        <p>Tout comportement discriminatoire, agressif, ou de nature à mettre en danger la sécurité d'autrui est strictement interdit et entraîne la suspension immédiate du compte, sans préjudice de poursuites judiciaires.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">6. Notation et avis</h2>
        <p>Après chaque trajet, conducteur et passager peuvent se noter mutuellement (1 à 5 étoiles + commentaire ≤ 500 caractères). Les avis calomnieux, injurieux ou hors sujet peuvent être supprimés à la demande de la partie concernée après examen.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">7. Responsabilité</h2>
        <p>Nold Go agit en qualité d'intermédiaire technique. Toute réclamation liée au comportement d'un utilisateur, à un incident routier ou à un dommage matériel doit être adressée directement aux personnes concernées et à leurs assurances respectives. Nold Go peut fournir, sur demande officielle des autorités, les informations d'identification en sa possession.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">8. Données personnelles</h2>
        <p>Les documents téléversés (permis, carte grise, pièce d'identité) sont conservés dans Emergent Object Storage, accessibles uniquement à l'équipe de vérification Nold Go et aux autorités compétentes sur demande motivée. Vous disposez d'un droit d'accès, de rectification et de suppression conformément au RGPD — contact : privacy@noldgo.com.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">9. Résiliation</h2>
        <p>Vous pouvez supprimer votre profil à tout moment depuis /covoiturage/profil. Les trajets futurs seront automatiquement annulés et remboursés. L'historique des trajets passés reste conservé pour raisons comptables et fiscales pendant 10 ans.</p>

        <h2 className="font-head text-xl font-black text-[#1A1A1A] mt-6 mb-2">10. Droit applicable</h2>
        <p>Les présentes conditions sont régies par le droit belge. Tout litige non résolu à l'amiable relève de la compétence exclusive des tribunaux de l'arrondissement de Bruxelles.</p>

        <p className="mt-8 text-xs text-[#75807A]">Nold Go — <a className="underline" href="mailto:contact@noldgo.com">contact@noldgo.com</a></p>
      </div>
    </div>
  );
}

// ================================================= CHAT MODAL
export function CarpoolChatModal({ bookingId, onClose, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);  // true = WebSocket ok, false = fallback polling
  const listRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const fetchAll = async () => {
    try {
      const { data } = await api.get(`/carpool/bookings/${bookingId}/messages`);
      setMessages(data.messages || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    // 1) Tente d'abord une connexion WebSocket (temps réel + pas de polling).
    // Fallback silencieux vers le polling HTTP si le WS échoue (proxy, offline, dev, etc.).
    const token = localStorage.getItem("token") || localStorage.getItem("access_token") || "";
    let closedByUs = false;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = backendUrl.replace(/^http/, "ws") + `/api/carpool/bookings/${bookingId}/ws-messages?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setLive(true);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "history") setMessages(data.messages || []);
          else if (data.type === "message") setMessages((prev) => {
            if (prev.some((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
        } catch (_) {}
      };
      ws.onclose = () => {
        setLive(false);
        wsRef.current = null;
        // Fallback polling si on n'a pas fermé volontairement
        if (!closedByUs && !pollRef.current) {
          fetchAll();
          pollRef.current = setInterval(fetchAll, 8000);
        }
      };
      ws.onerror = () => { /* onclose gérera le fallback */ };
    } catch (_) {
      // Ancien browser : passe direct au polling
      fetchAll();
      pollRef.current = setInterval(fetchAll, 8000);
    }
    return () => {
      closedByUs = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch(_){} wsRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // eslint-disable-next-line
  }, [bookingId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/carpool/bookings/${bookingId}/messages`, { text });
      setText("");
      // Optimistic append si le WS n'est pas connecté (le WS gérera le push sinon)
      if (!live && data?.message) {
        setMessages((prev) => prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4"
      data-testid="carpool-chat-modal" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2DFD8]">
          <div className="font-bold text-[#1A1A1A] inline-flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#059669]" /> Messagerie
            {live && <span data-testid="chat-live-dot" className="w-2 h-2 rounded-full bg-[#059669] animate-pulse" title="Temps réel actif" />}
          </div>
          <button onClick={onClose} className="text-[#75807A] hover:text-[#1A1A1A] p-1" data-testid="chat-close">✕</button>
        </div>
        <div ref={listRef} className="flex-1 overflow-auto px-3 py-3 space-y-2 bg-[#FDF8F0]" data-testid="chat-list">
          {messages.length === 0 && <p className="text-xs text-[#75807A] text-center py-8">Aucun message pour l'instant. Dites bonjour !</p>}
          {messages.map((m) => {
            const mine = m.from_user_id === currentUserId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#059669] text-white rounded-br-sm" : "bg-white border border-[#E2DFD8] rounded-bl-sm"}`}>
                  {!mine && <div className="text-[10px] font-bold text-[#F26419] mb-0.5">{m.from_name}</div>}
                  <div>{m.text}</div>
                  <div className={`text-[9px] mt-1 ${mine ? "text-white/60" : "text-[#75807A]"}`}>
                    {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 p-3 border-t border-[#E2DFD8]">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Écrivez un message…" maxLength={1000}
            className="flex-1 h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm" />
          <button data-testid="chat-send" onClick={send} disabled={busy || !text.trim()}
            className="px-4 h-11 rounded-xl bg-[#059669] hover:bg-[#047857] text-white text-sm font-bold disabled:opacity-50">
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
